import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getStoredConsent,
  pushConsentUpdate,
  shouldLoadGtmForConsent,
  shouldRequireConsentPrompt,
  storeConsent,
  type ConsentState,
} from '@/lib/analytics/consent'
import { initGtm } from '@/lib/analytics/initGtm'

type PreferenceState = {
  analytics: boolean
  ads: boolean
  personalization: boolean
  functionality: boolean
  security: boolean
}

const grantedState: ConsentState = {
  analytics: 'granted',
  ads: 'granted',
  personalization: 'granted',
  functionality: 'granted',
  security: 'granted',
}

const deniedState: ConsentState = {
  analytics: 'denied',
  ads: 'denied',
  personalization: 'denied',
  functionality: 'denied',
  security: 'denied',
}

function ConsentBanner() {
  const [isOpen, setIsOpen] = useState(false)
  const [isManageOpen, setIsManageOpen] = useState(false)
  const [preferences, setPreferences] = useState<PreferenceState>({
    analytics: true,
    ads: true,
    personalization: true,
    functionality: true,
    security: true,
  })

  const applyConsent = useCallback((state: ConsentState, persist = true) => {
    if (persist) {
      storeConsent(state)
    }

    if (shouldLoadGtmForConsent(state)) {
      initGtm()
    }

    pushConsentUpdate(state)
    setIsOpen(false)
    setIsManageOpen(false)
  }, [])

  useEffect(() => {
    const stored = getStoredConsent()
    if (stored) {
      applyConsent(stored, false)
      return
    }

    if (shouldRequireConsentPrompt()) {
      setIsOpen(true)
      return
    }

    applyConsent(grantedState)
  }, [applyConsent])

  if (!isOpen) {
    return null
  }

  return (
    <>
      <aside
        aria-label="Cookie consent"
        className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-dark bg-gray-darkest px-6 py-6 text-white shadow-lg"
      >
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <p className="text-body-md font-archivo font-light text-gray-lightest">
              We use cookies to improve your experience, measure performance, and
              support our marketing. You can accept, reject, or manage your
              preferences.
            </p>
            <Link
              to="/privacy-policy"
              className="text-body-sm font-archivo font-light text-brand-greenBright underline"
            >
              View privacy policy
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="min-h-[44px] rounded-[2px] bg-brand-greenBright px-5 text-caption-bold font-archivo font-bold uppercase text-brand-green"
              onClick={() => applyConsent(deniedState)}
            >
              Reject
            </button>
            <button
              type="button"
              className="min-h-[44px] rounded-[2px] bg-brand-greenBright px-5 text-caption-bold font-archivo font-bold uppercase text-brand-green"
              onClick={() => setIsManageOpen(true)}
            >
              Manage
            </button>
            <button
              type="button"
              className="min-h-[44px] rounded-[2px] bg-brand-greenBright px-5 text-caption-bold font-archivo font-bold uppercase text-brand-green"
              onClick={() => applyConsent(grantedState)}
            >
              Accept
            </button>
          </div>
        </div>
      </aside>

      {isManageOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-preferences-title"
            className="w-full max-w-[520px] rounded-[4px] bg-white p-6 text-gray-dark"
          >
            <h2 id="cookie-preferences-title" className="text-h3 font-archivo font-light text-black">
              Cookie preferences
            </h2>
            <p className="mt-2 text-body-sm font-archivo font-light text-gray-dark">
              Choose which cookies you want to allow. You can update these at any
              time.
            </p>

            <fieldset className="mt-6 space-y-4">
              <legend className="sr-only">Cookie categories</legend>
              <label className="flex items-center justify-between gap-4">
                <span className="text-body-md font-archivo font-light text-gray-dark">
                  Analytics
                </span>
                <input
                  type="checkbox"
                  className="size-5 accent-brand-green"
                  checked={preferences.analytics}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      analytics: event.target.checked,
                    }))
                  }
                />
              </label>
              <label className="flex items-center justify-between gap-4">
                <span className="text-body-md font-archivo font-light text-gray-dark">
                  Ads
                </span>
                <input
                  type="checkbox"
                  className="size-5 accent-brand-green"
                  checked={preferences.ads}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      ads: event.target.checked,
                    }))
                  }
                />
              </label>
              <label className="flex items-center justify-between gap-4">
                <span className="text-body-md font-archivo font-light text-gray-dark">
                  Personalization
                </span>
                <input
                  type="checkbox"
                  className="size-5 accent-brand-green"
                  checked={preferences.personalization}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      personalization: event.target.checked,
                    }))
                  }
                />
              </label>
              <label className="flex items-center justify-between gap-4">
                <span className="text-body-md font-archivo font-light text-gray-dark">
                  Functionality
                </span>
                <input
                  type="checkbox"
                  className="size-5 accent-brand-green"
                  checked={preferences.functionality}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      functionality: event.target.checked,
                    }))
                  }
                />
              </label>
              <label className="flex items-center justify-between gap-4">
                <span className="text-body-md font-archivo font-light text-gray-dark">
                  Security
                </span>
                <input
                  type="checkbox"
                  className="size-5 accent-brand-green"
                  checked={preferences.security}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      security: event.target.checked,
                    }))
                  }
                />
              </label>
            </fieldset>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                className="min-h-[40px] rounded-[2px] border border-gray-light px-4 text-caption-bold font-archivo font-bold uppercase text-gray-dark"
                onClick={() => setIsManageOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="min-h-[40px] rounded-[2px] bg-brand-green px-4 text-caption-bold font-archivo font-bold uppercase text-white"
                onClick={() =>
                  applyConsent({
                    analytics: preferences.analytics ? 'granted' : 'denied',
                    ads: preferences.ads ? 'granted' : 'denied',
                    personalization: preferences.personalization ? 'granted' : 'denied',
                    functionality: preferences.functionality ? 'granted' : 'denied',
                    security: preferences.security ? 'granted' : 'denied',
                  })
                }
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default ConsentBanner
